import { useEffect, useState } from 'react';
import { getOutingStats } from 'src/brain/api';
import { getCancellationView } from 'src/utils/cancellationView.js';

/**
 * Fetch per-hut cancellation stats for an NZ Great Walk and merge them into a
 * single walk-level view. Openings on the same date are summed across huts —
 * a bed reopening on any hut counts.
 *
 * Shape returned:
 *   {
 *     view: {
 *       dateCounts, totalSpots, lastUpdated, peakSeason,
 *       outingName, isLast30Days,
 *     } | null,
 *     loading: boolean,
 *   }
 *
 * @param {{ name?: string, huts?: Array<{ outingId: string, entityKey: string }> } | null | undefined} walk
 */
export function useNzGreatWalkCancellationSummary(walk) {
  const huts = walk?.huts || [];
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!huts.length) {
      setView(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const results = await Promise.all(
          huts.map((hut) =>
            getOutingStats({ outingId: hut.outingId, entityKey: hut.entityKey })
              .then((resp) => resp?.success || resp)
              .catch(() => null)
          )
        );
        if (cancelled) return;
        setView(mergeHutViews(results, walk));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // Huts identity is stable per walk prop; effect only re-runs when walk changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walk?.slug]);

  return { view, loading };
}

/**
 * Merge per-hut cancellation news into a single walk-level view. Openings on
 * the same date are summed across huts.
 */
function mergeHutViews(hutResults, walk) {
  const merged = {
    dateCounts: {},
    totalSpots: 0,
    lastUpdated: null,
    peakSeason: null,
    outingName: walk?.name || '',
    isLast30Days: false,
  };
  let sawAny = false;
  let anyIsLast30Days = false;
  for (const news of hutResults) {
    if (!news) continue;
    const v = getCancellationView(news);
    if (!v) continue;
    sawAny = true;
    if (v.isLast30Days) anyIsLast30Days = true;
    if (v.peakSeason) merged.peakSeason = v.peakSeason;
    if (v.lastUpdated) {
      const ts = new Date(v.lastUpdated).getTime();
      const prev = merged.lastUpdated ? new Date(merged.lastUpdated).getTime() : 0;
      if (ts > prev) merged.lastUpdated = v.lastUpdated;
    }
    for (const [date, count] of Object.entries(v.dateCounts || {})) {
      const n = Number(count) || 0;
      merged.dateCounts[date] = (merged.dateCounts[date] || 0) + n;
      merged.totalSpots += n;
    }
  }
  if (!sawAny) return null;
  merged.isLast30Days = anyIsLast30Days;
  return merged;
}
